/**
 * The trip this particular tool is on, on its own page.
 *
 * The board answers "what is moving"; somebody on a mould's page has already
 * picked the tool and wants the one thing the status badge cannot tell them -
 * IN_TRANSIT says it is between places, not which two.
 *
 * Absent rather than empty when there is no trip: a card saying "no journey"
 * on every one of a hundred moulds that never leave the building is noise on
 * every page to be useful on a few.
 */
import { useEffect, useState } from 'react';
import { Route as RouteIcon } from 'lucide-react';
import type { JourneyRow } from '@mouldtrack/shared';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/ui/primitives.js';
import { Button } from '../../components/ui/button.js';
import { listJourneys } from '../../api/client.js';
import { JourneyTimeline } from './JourneyTimeline.js';
import { whereIs } from './JourneysPage.js';

export function MouldJourneyCard({ mouldId }: { mouldId: string }) {
  const [journey, setJourney] = useState<JourneyRow | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    void listJourneys(`?mouldId=${encodeURIComponent(mouldId)}`)
      .then((rows) => {
        if (cancelled) return;
        // The open one if there is one, otherwise the most recent, so a tool
        // that arrived yesterday still shows where it went.
        const open = rows.find((r) => r.status === 'PLANNED' || r.status === 'IN_PROGRESS');
        setJourney(open ?? rows[0] ?? null);
      })
      // Journeys are online-only. Offline on a mould page is normal, and the
      // rest of the page is still worth reading.
      .catch(() => {
        if (!cancelled) setJourney(null);
      });
    return () => {
      cancelled = true;
    };
  }, [mouldId]);

  if (!journey) return null;

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RouteIcon className="size-4 text-muted-foreground" aria-hidden />
          Journey
        </CardTitle>
        <Button variant="ghost" size="sm" asChild>
          <a href="/journeys">All journeys</a>
        </Button>
      </CardHeader>
      <CardBody>
        <p className="mb-3 text-sm text-muted-foreground">{whereIs(journey)}</p>
        <JourneyTimeline journey={journey} />
      </CardBody>
    </Card>
  );
}
